/**
 * LanguageSelector Component
 * Requirement 49: Multi-language support for questionnaire
 *
 * Provides a dropdown to select the preferred language for the questionnaire.
 * Supported languages:
 * - English
 * - Indonesian (Bahasa Indonesia)
 * - Mongolian (Монгол хэл)
 * - Nepali (नेपाली)
 * - Vietnamese (Tiếng Việt)
 */
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  Box,
  Typography,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import { SUPPORTED_LANGUAGES, setStoredLanguage } from '../../../i18n/config';

interface LanguageSelectorProps {
  /** Additional CSS class name */
  className?: string;
  /** Whether to show only non-Japanese languages */
  excludeJapanese?: boolean;
  /** Size variant */
  size?: 'small' | 'medium';
  /** Whether to show the language icon */
  showIcon?: boolean;
  /** Callback when language changes */
  onLanguageChange?: (languageCode: string) => void;
  /** Custom font size in pixels */
  fontSize?: number;
  /** Whether to auto-adjust width based on selected language */
  autoWidth?: boolean;
  /** Use compact design matching the questionnaire style */
  compact?: boolean;
  /** Whether to use full width (icon + select fills container) */
  fullWidth?: boolean;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  className,
  excludeJapanese = false,
  size = 'small',
  showIcon = true,
  onLanguageChange,
  fontSize,
  autoWidth = false,
  compact = false,
  fullWidth = false,
}) => {
  const { i18n } = useTranslation();

  // Filter languages based on excludeJapanese prop
  const availableLanguages = excludeJapanese
    ? SUPPORTED_LANGUAGES.filter((lang) => lang.code !== 'ja')
    : SUPPORTED_LANGUAGES;

  const handleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newLang = event.target.value;
      i18n.changeLanguage(newLang);
      setStoredLanguage(newLang);

      // Update document lang attribute for accessibility
      document.documentElement.lang = newLang;

      // Call optional callback
      onLanguageChange?.(newLang);
    },
    [i18n, onLanguageChange]
  );

  // Don't render if no languages available
  if (availableLanguages.length === 0) {
    return null;
  }

  // Compact design for questionnaire screen
  if (compact) {
    return (
      <Box
        className={className}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: fullWidth ? 'flex-end' : 'flex-start',
          gap: '6px',
          width: fullWidth ? '100%' : 'auto',
        }}
      >
        {showIcon && (
          <LanguageIcon
            sx={{
              color: '#0068B7',
              fontSize: '18px',
              flexShrink: 0,
            }}
          />
        )}
        <FormControl size="small" sx={{ flexGrow: fullWidth ? 1 : 0 }}>
          <Select
            value={i18n.language}
            onChange={handleChange}
            displayEmpty
            aria-label="Select language"
            sx={{
              backgroundColor: '#fff',
              borderRadius: '6px',
              border: '1px solid #ccc',
              minWidth: fullWidth ? 'auto' : '150px',
              width: fullWidth ? '100%' : 'auto',
              height: '34px',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '& .MuiSelect-select': {
                py: '2px',
                px: '10px',
                display: 'flex',
                alignItems: 'center',
                fontSize: '17px',
                fontWeight: 500,
                color: '#0068B7',
              },
              '& .MuiSelect-icon': {
                color: '#666',
                right: '6px',
                fontSize: '18px',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  '& .MuiMenuItem-root': {
                    fontSize: '16px',
                    color: '#0068B7',
                    minHeight: '36px',
                  },
                },
              },
            }}
          >
            {availableLanguages.map((lang) => (
              <MenuItem key={lang.code} value={lang.code}>
                <Typography
                  component="span"
                  sx={{
                    fontFamily:
                      lang.code === 'mn'
                        ? "'Noto Sans Mongolian', 'Noto Sans', sans-serif"
                        : lang.code === 'ne'
                          ? "'Noto Sans Devanagari', 'Noto Sans', sans-serif"
                          : "'Noto Sans', sans-serif",
                    fontSize: 'inherit',
                    color: 'inherit',
                  }}
                >
                  {lang.nativeLabel}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  }

  // Default design
  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {showIcon && (
        <LanguageIcon
          sx={{
            color: 'text.secondary',
            fontSize: size === 'small' ? 20 : 24,
          }}
        />
      )}
      <FormControl size={size} sx={{ minWidth: autoWidth ? 'auto' : 120 }}>
        <Select
          value={i18n.language}
          onChange={handleChange}
          displayEmpty
          aria-label="Select language"
          sx={{
            '& .MuiSelect-select': {
              py: size === 'small' ? 0.75 : 1,
              display: 'flex',
              alignItems: 'center',
              fontSize: fontSize ? `${fontSize}px` : undefined,
            },
            minWidth: autoWidth ? 'auto' : undefined,
            width: autoWidth ? 'fit-content' : undefined,
          }}
        >
          {availableLanguages.map((lang) => (
            <MenuItem key={lang.code} value={lang.code}>
              <Typography
                component="span"
                sx={{
                  fontFamily:
                    lang.code === 'mn'
                      ? "'Noto Sans Mongolian', 'Noto Sans', sans-serif"
                      : lang.code === 'ne'
                        ? "'Noto Sans Devanagari', 'Noto Sans', sans-serif"
                        : "'Noto Sans', sans-serif",
                }}
              >
                {lang.nativeLabel}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default LanguageSelector;
